/*
 * Slot selection helpers for the Slots page (U4d/U4e UX).
 *
 * WHY THIS EXISTS
 * ---------------
 * Selecting 100+ slots by ticking 100 checkboxes is both slow and error-prone: the operator cannot
 * see what they picked, and one mis-click is invisible. Operators already think in RANGES - the
 * legacy "Update Bulk Settings" CSV taught the grammar `slot5-10, slot13` - so the fastest and
 * least error-prone input is that same grammar, plus a read-back that collapses whatever is
 * selected into ranges so a stray pick is visible at a glance.
 *
 * Pure functions, no React, no network: everything here is deterministic and unit-testable.
 */

/**
 * Sanity ceiling on how many numbers ONE range token may name. This is NOT a limit on how many
 * slots you can select - see the envelope trim below, which is what actually bounds the work.
 *
 * An earlier build expanded ranges blindly, so "1-9999999" built ~10 million strings synchronously
 * on every keystroke (measured: ~10 s of blocked main thread; eight digits killed the tab and took
 * the whole in-progress selection with it). The fix is the trim, not the ceiling: 99999 is simply
 * past any plausible slot number, so a token wider than that is a mashed keyboard rather than an
 * intent, and saying so beats pretending to understand it.
 */
export const MAX_RANGE_SPAN = 99999;

/** How many not-owned numbers we bother listing back; beyond this only the count matters. */
export const MAX_UNKNOWN_REPORTED = 200;

/** Extracts the trailing number of a slot name ("slot12" -> 12). Null when there is none. */
export function slotNumberOf(name) {
  if (name == null) return null;
  const m = String(name).match(/(\d+)\s*$/);
  return m ? parseInt(m[1], 10) : null;
}

/** The one canonical key for a slot everywhere in the UI. */
export function slotKey(slot) {
  if (!slot) return '';
  return slot.slot_name || slot.slot_id || (slot.slot_number != null ? `slot${slot.slot_number}` : '');
}

/**
 * Ranked search over pre-built picker rows: `{name, slot, deviceName, searchText}`.
 *
 * Ranking is not a nicety here, it is the difference between finding a slot and being told it does
 * not exist. A capped list sliced off an UNRANKED result set can drop the exact thing that was
 * typed: on a 10,100-slot account "bella" matches 595 rows, and the account actually called
 * @bella can sit past any sane cap purely because its slot number is high.
 *
 * Tiers, best first: exact slot id or exact @username; then either of those as a prefix; then a
 * label prefix; then a plain substring anywhere. Array#sort is stable, so within one tier the rows
 * keep the slot-number order they arrived in.
 *
 * @returns {{rows: object[], total: number}} `rows` is capped, `total` is how many really matched.
 */
export function rankSlotMatches(rows, query, limit) {
  const all = rows || [];
  const q = String(query == null ? '' : query).trim().toLowerCase();
  const cap = limit == null ? all.length : limit;
  if (!q) return { rows: all.slice(0, cap), total: all.length };

  const terms = q.split(/\s+/);
  const hits = [];
  for (let i = 0; i < all.length; i++) {
    const r = all[i];
    // searchText is precomputed by the caller; fall back so this stays usable on raw rows.
    const hay = r.searchText != null ? r.searchText : slotSearchText(r.slot, r.deviceName);
    if (!terms.every((t) => hay.includes(t))) continue;
    const name = String(r.name || '').toLowerCase();
    const user = String((r.slot && r.slot.ig_username) || '').toLowerCase();
    const label = String((r.slot && r.slot.slot_label) || '').toLowerCase();
    let score = 1;
    if (name === q || user === q) score = 4;
    else if (name.startsWith(q) || user.startsWith(q)) score = 3;
    else if (label.startsWith(q)) score = 2;
    hits.push({ r, score });
  }
  hits.sort((a, b) => b.score - a.score);
  return { rows: hits.slice(0, cap).map((h) => h.r), total: hits.length };
}

/**
 * Parses a plain number-range expression into concrete ids: `1-5, 7, 51-3000`.
 *
 * This is the shape the LEGACY bulk-update box already speaks ("Copying only settings 1-5 and 7 to
 * slots..."), so anyone who used the old page types the same thing here and it means the same
 * thing. Separators are commas, spaces, semicolons or newlines; en/em dashes count as `-`; a
 * reversed range is read forwards.
 *
 * Unlike parseSlotRanges this does NOT trim over-wide ranges, because there is no "highest id you
 * own" to trim to - ids that do not exist are simply reported as `unknown` and dropped. `51-3000`
 * against a 637-setting server is therefore a legitimate way to say "everything from 51 up".
 *
 * @param expr the raw text
 * @param knownIds every id the server actually has (numbers); when omitted nothing is dropped
 * @returns {{ids: number[], unknown: number[], invalid: string[], requested: number}}
 */
export function parseNumberRanges(expr, knownIds) {
  const out = { ids: [], unknown: [], invalid: [], requested: 0 };
  if (expr == null) return out;
  const known = knownIds ? new Set(Array.from(knownIds, Number)) : null;
  const seen = new Set();
  const unknownSeen = new Set();

  String(expr).replace(/[‒–—−]/g, '-').split(/[,;\n\r\s]+/)
    .map((t) => t.trim()).filter(Boolean)
    .forEach((token) => {
      const range = token.match(/^(\d+)-(\d+)$/);
      const single = token.match(/^(\d+)$/);
      if (!range && !single) { out.invalid.push(token); return; }

      let from, to;
      if (single) { from = to = parseInt(single[1], 10); }
      else {
        from = parseInt(range[1], 10);
        to = parseInt(range[2], 10);
        if (from > to) { const t = from; from = to; to = t; }
      }
      // A pasted range can be enormous ("1-99999"); walking it is fine, but only ever emit ids
      // that exist, and never let a single token blow the loop up.
      if (to - from > 100000) { out.invalid.push(token); return; }
      for (let n = from; n <= to; n++) {
        out.requested++;
        if (known && !known.has(n)) {
          if (!unknownSeen.has(n)) { unknownSeen.add(n); out.unknown.push(n); }
          continue;
        }
        if (!seen.has(n)) { seen.add(n); out.ids.push(n); }
      }
    });

  out.ids.sort((a, b) => a - b);
  out.unknown.sort((a, b) => a - b);
  return out;
}

/**
 * Everything about a slot a person might type to find it, as one lower-cased haystack.
 *
 * `deviceName` is passed in rather than read off the slot because the row only carries the raw
 * `device_id` while every screen DISPLAYS the resolved friendly name. Matching only the raw id
 * means typing what you can see in the Device column finds nothing - which is exactly the kind of
 * search that teaches people the search is broken.
 *
 * The separator is a character no slot field can contain, so a query can never match across the
 * boundary between two fields.
 */
export function slotSearchText(slot, deviceName) {
  return [slotKey(slot), slot && slot.slot_label, slot && slot.ig_username,
    slot && slot.device_id, deviceName]
    .filter(Boolean).join(' ').toLowerCase();
}

/**
 * The ONE "does this slot match what was typed" predicate. Both the page's search box and the
 * bulk-copy source picker call it, so the two can never disagree about what a query means.
 *
 * Terms are ANDed and order-independent: `bella 12` finds slot12 belonging to @bella_shop whichever
 * way round it is typed. A single term behaves exactly like the plain substring match this
 * replaced, so nothing anyone had learned stops working.
 */
export function slotMatches(slot, query, deviceName) {
  const q = String(query == null ? '' : query).trim().toLowerCase();
  if (!q) return true;
  if (!slot) return false;
  const hay = slotSearchText(slot, deviceName);
  return q.split(/\s+/).every((term) => hay.includes(term));
}

/**
 * Parses a range expression into concrete slot names.
 *
 * Accepted: `5`, `5-40`, `slot5`, `slot5-slot40`, `slot 5 - 40`, separated by commas, spaces,
 * semicolons or newlines. En/em dashes count as `-` (people paste from notes). A reversed range
 * (`40-5`) is read as `5-40` rather than rejected - the intent is unambiguous.
 *
 * Everything is validated against the slots the user actually owns, so the caller can show
 * "42 matched, 3 you do not own" BEFORE anything happens.
 *
 * OVER-WIDE RANGES ARE TRIMMED, NOT REFUSED. Nobody should have to remember that their account
 * stops at slot 63 in order to say "all of them": `1-99999` means "everything I have". A range
 * that runs past the highest slot the account owns is trimmed to that slot and reported as
 * trimmed (`highestOwned`), never as thousands of complaints. A range that is entirely past the
 * end is not silently empty either - it lands in `outOfRange` so the caller can say where the
 * account actually stops. Holes are reported only for a range that fits INSIDE what the account
 * owns: `48-62` skipping slot51-59 is worth flagging, but a deliberate sweep to the end is not.
 * A single number is always checked (naming one slot that is not there is a typo worth showing).
 *
 * @param expr  the raw text
 * @param availableNames  every slot name the user owns
 * @returns {{names: string[], unknown: string[], invalid: string[], requested: number,
 *            unknownOverflow: number, trimmed: boolean, outOfRange: string[],
 *            highestOwned: number|null}}
 *          `requested` counts what was effectively asked for AFTER trimming.
 */
export function parseSlotRanges(expr, availableNames) {
  const out = {
    names: [], unknown: [], invalid: [], requested: 0, unknownOverflow: 0,
    trimmed: false, outOfRange: [], highestOwned: null,
  };
  if (!expr || !String(expr).trim()) return out;

  // name -> number, and number -> name, for the slots that exist
  const byNumber = new Map();
  const lowerNames = new Map();
  (availableNames || []).forEach((n) => {
    const num = slotNumberOf(n);
    if (num != null) {
      // Prefer the canonical `slotN`: two names can end in the same digits ("backup_slot5"), and
      // first-wins would resolve "5" by whatever order the API happened to return.
      const exact = String(n).toLowerCase() === `slot${num}`;
      if (!byNumber.has(num) || exact) byNumber.set(num, n);
    }
    lowerNames.set(String(n).toLowerCase(), n);
  });

  // The envelope of what the account actually owns. Anything outside it is not an error to argue
  // about - it simply is not there, so a sweep gets trimmed to it instead of expanded past it.
  // This is also what bounds the loop below: it can never run wider than the account is.
  let lowOwned = null;
  let highOwned = null;
  byNumber.forEach((_name, num) => {
    if (lowOwned == null || num < lowOwned) lowOwned = num;
    if (highOwned == null || num > highOwned) highOwned = num;
  });
  out.highestOwned = highOwned;

  const picked = new Set();
  const tokens = String(expr)
    .replace(/[‒–—−]/g, '-') // figure/en/em dash, minus sign -> hyphen
    .split(/[,;\n]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  tokens.forEach((token) => {
    // a range: "5-40" or "slot5-slot40" (spaces anywhere)
    const range = token.match(/^\s*(?:slot\s*)?(\d+)\s*-\s*(?:slot\s*)?(\d+)\s*$/i);
    if (range) {
      let a = parseInt(range[1], 10);
      let b = parseInt(range[2], 10);
      if (a > b) [a, b] = [b, a];
      if (b - a + 1 > MAX_RANGE_SPAN) {
        // Past any plausible slot number: this is a mashed key, not a range. See MAX_RANGE_SPAN.
        out.invalid.push(`${token} (over ${MAX_RANGE_SPAN} slots - typo?)`);
        return;
      }
      if (highOwned == null) {
        // Nothing numbered to match against (empty account, or still loading).
        out.outOfRange.push(token);
        return;
      }
      // Trim to what exists rather than complaining about what does not.
      const from = Math.max(a, lowOwned);
      const to = Math.min(b, highOwned);
      if (from > to) {
        out.outOfRange.push(token);
        return;
      }
      const sweptPastTheEnd = from !== a || to !== b;
      if (sweptPastTheEnd) out.trimmed = true;
      out.requested += to - from + 1;
      for (let n = from; n <= to; n++) {
        const name = byNumber.get(n);
        if (name) {
          picked.add(name);
        } else if (!sweptPastTheEnd) {
          // A hole inside a deliberate range is worth flagging; inside a sweep to the end it is not.
          if (out.unknown.length < MAX_UNKNOWN_REPORTED) out.unknown.push(`slot${n}`);
          else out.unknownOverflow++;
        }
      }
      return;
    }
    // a single number: "5" or "slot5"
    const single = token.match(/^\s*(?:slot\s*)?(\d+)\s*$/i);
    if (single) {
      out.requested += 1;
      const n = parseInt(single[1], 10);
      const name = byNumber.get(n);
      if (name) picked.add(name);
      else if (out.unknown.length < MAX_UNKNOWN_REPORTED) out.unknown.push(`slot${n}`);
      else out.unknownOverflow++;
      return;
    }
    // an exact slot name that carries no number (defensive: nothing in production looks like this)
    const exact = lowerNames.get(token.toLowerCase());
    if (exact) {
      out.requested += 1;
      picked.add(exact);
      return;
    }
    out.invalid.push(token);
  });

  out.names = sortByNumber([...picked]);
  out.unknown = [...new Set(out.unknown)];
  return out;
}

/** Slot names in numeric order (slot2 before slot10); non-numeric names sort last, alphabetically. */
export function sortByNumber(names) {
  return [...(names || [])].sort((x, y) => {
    const a = slotNumberOf(x);
    const b = slotNumberOf(y);
    if (a == null && b == null) return String(x).localeCompare(String(y));
    if (a == null) return 1;
    if (b == null) return -1;
    return a - b;
  });
}

/**
 * Collapses a selection into a human-readable range summary: ["slot5".."slot40", "slot55"] becomes
 * "slot5-slot40, slot55". This read-back IS the error check - a stray pick shows up as an extra
 * fragment the moment the operator glances at it.
 *
 * @param maxParts stop after this many fragments and append "+N more"
 */
export function collapseToRanges(names, maxParts = 6) {
  const sorted = sortByNumber(names);
  if (sorted.length === 0) return '';

  const parts = [];
  let runStart = null;
  let runEnd = null;
  const flush = () => {
    if (runStart == null) return;
    parts.push(runStart === runEnd ? runStart : `${runStart}-${runEnd}`);
    runStart = null;
    runEnd = null;
  };

  sorted.forEach((name) => {
    const n = slotNumberOf(name);
    if (n == null) {
      flush();
      parts.push(name);
      return;
    }
    if (runStart == null) {
      runStart = name;
      runEnd = name;
      return;
    }
    if (n === slotNumberOf(runEnd) + 1) {
      runEnd = name;
    } else {
      flush();
      runStart = name;
      runEnd = name;
    }
  });
  flush();

  if (parts.length <= maxParts) return parts.join(', ');
  return `${parts.slice(0, maxParts).join(', ')} +${parts.length - maxParts} more`;
}

/** Every name from `from` to `to` inclusive, in the given ORDERED list (for shift-click). */
export function namesBetween(orderedNames, from, to) {
  const i = orderedNames.indexOf(from);
  const j = orderedNames.indexOf(to);
  if (i < 0 || j < 0) return [];
  const [lo, hi] = i <= j ? [i, j] : [j, i];
  return orderedNames.slice(lo, hi + 1);
}

/** Splits a list into chunks of at most `size` (the server caps bulk-copy targets per request). */
export function chunk(list, size) {
  const out = [];
  for (let i = 0; i < (list || []).length; i += size) out.push(list.slice(i, i + size));
  return out;
}
